import React, { FunctionComponent, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { ensure } from "sprint-common-web";

import { getTvSeriesAssetsListHref } from "../../../../assets/AssetsRoutes";
import { Distributor } from "../../../../common/data/lookup-data-providers/DistributorsDataProvider";
import { VersionType } from "../../../../common/data/lookup-data-providers/VersionTypesDataProvider";
import { useAssetsManageTvSeriesApi } from "../../api/useAssetsManageTvSeriesApi";
import { EpisodeDetails, EpisodeVersionDetails, TvSeasonDetails, TvSeriesDetails } from "../../TvSeriesDetails";
import { TvSeriesContext } from "./TvSeriesContext";

type TvSeriesProviderProps = React.PropsWithChildren<unknown>;

export const TvSeriesProvider: FunctionComponent<TvSeriesProviderProps> = (props: TvSeriesProviderProps) => {
    const { children } = props;

    const { tvSeriesId } = useParams<{ tvSeriesId: string }>();
    const { getTvSeriesById } = useAssetsManageTvSeriesApi();
    const [tvSeries, setTvSeries] = useState<TvSeriesDetails>();
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const history = useHistory();

    useEffect(() => {
        refreshTvSeries();
    }, [tvSeriesId]);

    const refreshTvSeries = async (): Promise<void> => {
        setIsLoading(true);
        const response = await getTvSeriesById(tvSeriesId);

        setTvSeries({ ...response.item });
        setIsLoading(false);
    };

    const silentTvSeriesRefresh = async (onRefresh?: (newTvSeries: TvSeriesDetails) => void): Promise<void> => {
        const response = await getTvSeriesById(tvSeriesId);
        setTvSeries(() => {
            const newTvSeries = { ...response.item };
            if (onRefresh) {
                onRefresh(newTvSeries);
            }

            return newTvSeries;
        });
    };

    const setTvSeriesDetails = (title: string) => {
        ensure(tvSeries);

        setTvSeries({ ...tvSeries, title });
    };

    const addSeason = (newSeason: TvSeasonDetails): void => {
        ensure(tvSeries);

        const newSeasons = [...tvSeries.seasons, newSeason];

        setTvSeries({ ...tvSeries, seasons: newSeasons });
    };

    const updateSeason = (seasonId: string, name: string, seasonNumber: number): void => {
        const seasonToUpdate = tvSeries?.seasons.find((season: TvSeasonDetails) => season.id === seasonId);
        if (seasonToUpdate) {
            seasonToUpdate.name = name;
            seasonToUpdate.number = seasonNumber;
        }
        setTvSeries(tvSeries);
    };

    const addEpisode = (seasonId: string, newEpisode: EpisodeDetails): void => {
        const selectedSeason = tvSeries?.seasons.find((season: TvSeasonDetails) => season.id === seasonId);
        if (!selectedSeason) return;

        selectedSeason.episodes.push(newEpisode);

        setTvSeries(tvSeries);
    };

    const updateEpisode = (seasonId: string, episodeId: string, title: string, episodeNumber: number): void => {
        const selectedSeason = tvSeries?.seasons.find((season: TvSeasonDetails) => season.id === seasonId);
        if (!selectedSeason) return;

        const episodeToUpdate = selectedSeason.episodes.find((episode: EpisodeDetails) => episode.id === episodeId);
        if (episodeToUpdate) {
            episodeToUpdate.title = title;
            episodeToUpdate.number = episodeNumber;
        }

        setTvSeries(tvSeries);
    };

    const moveToTvSeriesAssets = (seasonId: string, episodeId?: string, versionTypeId?: string) => {
        const versionTypeIds = versionTypeId ? [versionTypeId] : [];

        const tvSeriesAssetsHref = getTvSeriesAssetsListHref(tvSeriesId, { seasonId, episodeId, versionTypeIds });

        history.push(tvSeriesAssetsHref);
    };

    const updateEpisodeVersion = (
        seasonId: string,
        episodeId: string,
        versionId: string,
        distributor: Distributor,
        code: string,
        type: VersionType
    ) => {
        const season = getSeasonById(seasonId, tvSeries?.seasons);
        const episode = getEpisodeById(episodeId, season?.episodes);
        const versionToUpdate = getEpisodeVersionById(versionId, episode?.versions);

        if (versionToUpdate) {
            versionToUpdate.distributor = distributor;
            versionToUpdate.code = code;
            versionToUpdate.type = type;
            versionToUpdate.isModified = true;
        }

        setTvSeries(tvSeries);
    };

    const addEpisodeVersion = (seasonId: string, episodeId: string, episodeVersion: EpisodeVersionDetails) => {
        setTvSeries((prevTvSeries: TvSeriesDetails | undefined) => {
            if (!prevTvSeries) throw new Error("Can't add Episode Version to undefined TvSeries");
            const season = getSeasonById(seasonId, prevTvSeries?.seasons);
            const episode = getEpisodeById(episodeId, season?.episodes);

            if (!episode) throw new Error("No episode with given Id");

            episode?.versions.push({ ...episodeVersion, isModified: true });

            return { ...prevTvSeries };
        });
    };

    const setVersionAsMerged = (seasonId: string, episodeId: string, versionId: string, mergedTargetId: string) => {
        setTvSeries((prevTvSeries: TvSeriesDetails | undefined) => {
            const season = getSeasonById(seasonId, prevTvSeries?.seasons);
            const episode = getEpisodeById(episodeId, season?.episodes);
            const versionToUpdate = getEpisodeVersionById(versionId, episode?.versions);

            if (!versionToUpdate || !prevTvSeries) return prevTvSeries;

            versionToUpdate.mergedTargetId = mergedTargetId;

            return { ...prevTvSeries };
        });
    };

    return (
        <TvSeriesContext.Provider
            value={{
                isLoading,
                tvSeries,
                setTvSeriesDetails,
                updateEpisodeVersion,
                addEpisodeVersion,
                addSeason,
                updateSeason,
                updateEpisode,
                addEpisode,
                moveToTvSeriesAssets,
                setVersionAsMerged,
                silentTvSeriesRefresh
            }}
        >
            {children}
        </TvSeriesContext.Provider>
    );
};

const getSeasonById = (id: string, seasons?: TvSeasonDetails[]): TvSeasonDetails | undefined => {
    return seasons?.find((season: TvSeasonDetails) => season.id === id);
};

const getEpisodeById = (id: string, episodes?: EpisodeDetails[]): EpisodeDetails | undefined => {
    return episodes?.find((episode: EpisodeDetails) => episode.id === id);
};

const getEpisodeVersionById = (
    id: string,
    episodeVersions?: EpisodeVersionDetails[]
): EpisodeVersionDetails | undefined => {
    return episodeVersions?.find((episodeVersion: EpisodeVersionDetails) => episodeVersion.id === id);
};
